import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import DataTable from "../../../components/common/datatable/DataTable";
import HeaderWithInfo from "../../../components/common/datatable/HeaderWithInfo";
import "./AFCAnalysisTable.css";
import { formatDateToDDMMYYYY, compareDateValues } from "../../../utils/dateUtils";
const normalizeRole = (role) => (role || "").replace(/\s+/g, "").toLowerCase();
const hasRole = (rawRole, target) =>
  normalizeRole(rawRole).split(",").some((r) => r === target);
const COLUMN_INFO = {
  totalSupplierForecastOriginal: "Original supplier forecast published for the week.",
  totalSupplierForecastCurrent: "Current SAP forecast -",
  totalSupplierConfirmation: "Total supplier confirmation for the week.",
};
const hasDateValue = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "-" && value !== "—";

const getPublishedDate = (row) =>
  row?.publishedDate ??
  row?.publicationDate ??
  row?.PUBLICATION_DATE ??
  row?.publication_date ??
  row?.creationDate ??
  row?.CREATION_DATE ??
  row?.creation_date;

const getCurrentForecastDate = (row) =>
  row?.currentForecastDate ??
  row?.CURRENT_FORECAST_DATE ??
  row?.current_forecast_date;

const formatSupplierConfirmation = (value) =>
  value === null ||
  value === undefined ||
  value === "-" ||
  value === "—" ||
  value === "" ||
  Number.isNaN(value)
    ? "N/A"
    : value;

const NumberSpinnerRenderer = (props) => {
  const parsedValue = Number(props.node?.data?.simulateTotalSupplierConfirmation ?? props.value);
  const committedValue = Number.isFinite(parsedValue) ? parsedValue : 0;
  const isDisabled = Boolean(props.isGlobalViewer);
  const applySimulateUpdate = props.applySimulateUpdate;

  const [draftValue, setDraftValue] = useState(String(committedValue));
  const isEditingRef = useRef(false);
  const suppressBlurCommitRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftValue(String(committedValue));
    }
  }, [committedValue]);

  const stopGridEvent = (event) => {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const stopGridPropagation = (event) => {
    if (!event) return;
    event.stopPropagation();
  };

  const commitValue = (rawValue) => {
    if (isDisabled || !applySimulateUpdate) return;
    const normalized = rawValue === "" || rawValue === "-" ? 0 : Number(rawValue);
    if (!Number.isFinite(normalized)) return;
    applySimulateUpdate(props.api, props.node, normalized);
    setDraftValue(String(normalized));
  };

  const getCurrentValue = () => {
    const current = Number(props.node?.data?.simulateTotalSupplierConfirmation ?? props.value);
    return Number.isFinite(current) ? current : 0;
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", paddingTop: 6 }}>
      <button
        type="button"
        style={{ width: 22, height: 22, fontWeight: "bold", cursor: "pointer" }}
        onMouseDown={(event) => {
          suppressBlurCommitRef.current = true;
          stopGridEvent(event);
        }}
        onClick={(event) => {
          stopGridEvent(event);
          const nextValue = getCurrentValue() - 1;
          setDraftValue(String(nextValue));
          commitValue(String(nextValue));
          suppressBlurCommitRef.current = false;
        }}
        aria-label="decrement"
        disabled={isDisabled}
      >
        -
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={draftValue}
        style={{ width: 48, textAlign: "center", height: 22 }}
        onMouseDown={stopGridPropagation}
        onClick={stopGridPropagation}
        onFocus={() => {
          isEditingRef.current = true;
        }}
        onChange={(e) => {
          const nextRaw = e.target.value;
          if (!/^-?\d*$/.test(nextRaw)) return;
          setDraftValue(nextRaw);
          if (nextRaw !== "" && nextRaw !== "-") {
            commitValue(nextRaw);
          }
        }}
        onBlur={() => {
          isEditingRef.current = false;
          if (suppressBlurCommitRef.current) {
            suppressBlurCommitRef.current = false;
            return;
          }
          commitValue(draftValue);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commitValue(draftValue);
            event.currentTarget.blur();
          }
        }}
        disabled={isDisabled}
      />
      <button
        type="button"
        style={{ width: 22, height: 22, fontWeight: "bold", cursor: "pointer" }}
        onMouseDown={(event) => {
          suppressBlurCommitRef.current = true;
          stopGridEvent(event);
        }}
        onClick={(event) => {
          stopGridEvent(event);
          const nextValue = getCurrentValue() + 1;
          setDraftValue(String(nextValue));
          commitValue(String(nextValue));
          suppressBlurCommitRef.current = false;
        }}
        aria-label="increment"
        disabled={isDisabled}
      >
        +
      </button>
    </div>
  );
};

const AFCAnalysisTable = ({ rows = [], onSimulateChange, onRowsUpdate, formatWeekLabel, hiddenColumns = [], publishedDateOverride, currentForecastDateOverride }) => {
 const pagination = true;
 const paginationPageSize = 250;
const paginationPageSizeSelector = [250, 500, 750, 1000];
const rawRole = localStorage.getItem("appUserRole") || "";
const isGlobalViewer = (hasRole(rawRole, "globalviewer") || hasRole(rawRole, "viewer"))
  && !hasRole(rawRole, "appadmin") && !hasRole(rawRole, "globalplanner") && !hasRole(rawRole, "planner");

const publishedDateInfo = useMemo(() => {
  if (publishedDateOverride) return publishedDateOverride;
  const firstRow = rows?.find((row) => hasDateValue(getPublishedDate(row))) || null;
  const dateValue = getPublishedDate(firstRow);
  return hasDateValue(dateValue) ? formatDateToDDMMYYYY(dateValue) : "N/A";
}, [rows, publishedDateOverride]);

const currentForecastDateInfo = useMemo(() => {
  if (currentForecastDateOverride) return currentForecastDateOverride;
  const firstRow = rows?.find((row) => hasDateValue(getCurrentForecastDate(row))) || null;
  const dateValue = getCurrentForecastDate(firstRow);
  return hasDateValue(dateValue) ? formatDateToDDMMYYYY(dateValue) : "N/A";
}, [rows, currentForecastDateOverride]);

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const calcSsBreach = (projStockBasedOnConfirmation, safetyStock) =>
  toNumber(projStockBasedOnConfirmation) < toNumber(safetyStock) ? "YES" : "NO";

const calcStockout = (projStockBasedOnConfirmation) =>
  toNumber(projStockBasedOnConfirmation) > 0 ? "NO" : "YES";

const applySimulateUpdate = useCallback((api, node, nextValueRaw) => {
  if (!api || !node) return;

  const editedRowIndex = node.rowIndex ?? 0;
  const oldValue = Number(node.data?.simulateTotalSupplierConfirmation);
  const nextValue = Number(nextValueRaw);
  const safeOld = Number.isFinite(oldValue) ? oldValue : 0;
  const safeNew = Number.isFinite(nextValue) ? nextValue : 0;
  const diff = safeNew - safeOld;

  // Replace row data so React cell renderer gets a fresh object and re-renders visibly.
  const nextNodeData = {
    ...(node.data || {}),
    __skipOnSimulate: true,
    simulateTotalSupplierConfirmation: safeNew,
    simulateSupplierQuantityConfirmation: safeNew,
  };
  node.setData(nextNodeData);
  api.refreshCells({ rowNodes: [node], columns: ["simulateTotalSupplierConfirmation"], force: true });

  api.forEachNode((iterNode) => {
    if ((iterNode.rowIndex ?? 0) < editedRowIndex) return;

    const currentCumulative = Number(iterNode.data?.supplierConfirmationCumulative ?? 0);
    const updatedCumulative = currentCumulative + diff;
    iterNode.setDataValue("supplierConfirmationCumulative", updatedCumulative);

    const cumulativeForecastOriginal = Number(iterNode.data?.cumulativeForecastOriginal ?? 0);
    iterNode.setDataValue("deltaCumulativeOriginal", updatedCumulative - cumulativeForecastOriginal);

    const cumulativeForecastCurrent = Number(iterNode.data?.cumulativeForecastCurrent ?? 0);
    iterNode.setDataValue("deltaCumulativeCurrent", updatedCumulative - cumulativeForecastCurrent);

    const currentProj = Number(iterNode.data?.projStockBasedOnConfirmation ?? 0);
    const updatedProj = currentProj + diff;
    iterNode.setDataValue("projStockBasedOnConfirmation", updatedProj);

    const safetyStock = Number(iterNode.data?.safetyStock ?? 0);
    iterNode.setDataValue("ssBreach", calcSsBreach(updatedProj, safetyStock));
    iterNode.setDataValue("stockout", calcStockout(updatedProj));
  });

  const updatedRows = [];
  api.forEachNode((iterNode) => updatedRows.push({ ...iterNode.data }));
  onRowsUpdate?.(updatedRows);
  onSimulateChange?.(node.data?.id, safeNew);
}, [onRowsUpdate, onSimulateChange, calcSsBreach, calcStockout]);

  const baseColumns = useMemo(
    () => [
      {
        field: "Week of GR Date",
        headerName: "Week of delivery date(GR)",
        minWidth: 140,
        comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
        filterParams: {
          comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
        },
      },
    
      {
        field: "totalSupplierForecastOriginal",
        headerName: "Supplier Forecast Published",
        flex: 1,
        minWidth: 200,
        headerComponent: HeaderWithInfo,
        headerComponentParams: {
          infoText: `Forecast Published Date: ${publishedDateInfo}`,
        },
      },
      {
        field: "totalSupplierForecastCurrent",
        headerName: "Current SAP Forecast",
        flex: 1,
        minWidth: 200,
        headerComponent: HeaderWithInfo,
        headerComponentParams: { infoText: `Current SAP Forecast Date: ${currentForecastDateInfo}` },
      },
      {
        field: "totalSupplierConfirmation",
        headerName: "Total Supplier Confirmation",
        flex: 1,
        minWidth: 170,
       
        valueFormatter: (params) => formatSupplierConfirmation(params.value),
      },

{
  field: "simulateTotalSupplierConfirmation",
  headerName: "Simulate Total Supplier Confirmation",
  minWidth: 180,
  cellClass: "no-focus-border",
   headerClass: "simulate-column-header",
  cellClass: "no-focus-border simulate-column-cell",
  cellRenderer: NumberSpinnerRenderer,
  cellRendererParams: {
    isGlobalViewer,
    applySimulateUpdate,
  },
},
         {
      headerName: "Total Firm Supply",
      marryChildren: true,
      children: [
        {
          field: "totalFirmSupply",
          headerName: "Total",
          columnGroupShow: "closed", // visible when collapsed
          minWidth: 140,
        },
        {
          field: "poByGrDate",
          headerName: "PO by GR Date",
          columnGroupShow: "open",
          minWidth: 160,
          valueFormatter: (params) => {
            const value = params?.value;
            return value === null ||
              value === undefined ||
              value === "" ||
              value === "-" ||
              value === "—" ||
              Number.isNaN(value)
              ? "N/A"
              : value;
          },
        },
        {
          field: "poPastDue",
          headerName: "PO Past Due",
          columnGroupShow: "open",
          minWidth: 160,
          valueFormatter: (params) => {
            const value = params?.value;
            return value === null ||
              value === undefined ||
              value === "" ||
              value === "-" ||
              value === "—" ||
              Number.isNaN(value)
              ? "N/A"
              : value;
          },
        },
      ],
    },


    
      {
        field: "safetyStock",
        headerName: "Safety Stock",
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) =>
          params.value === null || params.value === undefined ? "N/A" : params.value,
      },
      { field: "supplierConfirmationCumulative", headerName: "Supplier Confirmation(Cumulative)", flex: 1 ,minWidth: 220},
      { field: "cumulativeForecastOriginal", headerName: "Cumulative Supplier Forecast Published", flex: 1 ,minWidth: 200},
      { field: "cumulativeForecastCurrent", headerName: "Cumulative Current SAP Forecast", flex: 1 ,minWidth: 200},
      { field: "deltaCumulativeOriginal", headerName: "Delta (Cumulative) Supplier Forecast Published", flex: 1, minWidth: 200 },
      { field: "deltaCumulativeCurrent", headerName: "Delta (Cumulative) Current SAP Forecast", flex: 1, minWidth: 200 },
      {field:"projStockBasedOnConfirmation", headerName:"Proj. Stock Based On Confirmation", flex:1, minWidth:200},
      { field: "ssBreach", headerName: "SS Breach?", flex: 1, minWidth: 160 },
       { field: "stockout", headerName: "Stockout?", flex: 1, minWidth: 160 },
    ],
    [NumberSpinnerRenderer, publishedDateInfo, currentForecastDateInfo, publishedDateOverride, currentForecastDateOverride]
  );

  const hiddenFieldSet = useMemo(() => new Set(hiddenColumns || []), [hiddenColumns]);

  const columns = useMemo(() => {
    const applyHidden = (cols = []) =>
      cols.map((col) => {
        if (col.children) {
          return { ...col, children: applyHidden(col.children) };
        }
        if (!col.field) return col;
        return { ...col, hide: hiddenFieldSet.has(col.field) };
      });

    return applyHidden(baseColumns);
  }, [baseColumns, hiddenFieldSet]);


  // Helper to show dash for null/undefined
  const dashIfNull = (val) => (val === null || val === undefined ? 'N/A' : val);

  const tableData = useMemo(() => {
    return (rows || []).map((r, idx) => ({
      id: r.afcSequenceId ?? idx,
      weekStartDate: r.weekStartDate,
      weekEndDate: r.weekEndDate,
      "Week of GR Date":
        r["Week of GR Date"] ??
        (typeof formatWeekLabel === "function"
          ? formatWeekLabel(r)
          : r.weekStartDate && r.weekEndDate
            ? `${r.weekStartDate} - ${r.weekEndDate}`
            : r.weekStartDate || "N/A"),
      creationDate:
        r.creationDate ?? r.CREATION_DATE ?? r.creation_date ?? r.creationdate,
      publishedDate:
        r.publishedDate ?? r.publicationDate ?? r.PUBLICATION_DATE ?? r.publication_date,
      currentForecastDate:
        r.currentForecastDate ?? r.CURRENT_FORECAST_DATE ?? r.current_forecast_date,

      totalSupplierForecastOriginal:
        r.totalSupplierForecastOriginal ?? r.originalForecast ?? 0,
      totalSupplierForecastCurrent:
        r.totalSupplierForecastCurrent ?? r.sumOfForest ?? r.originalForecast ?? 0,
      totalSupplierConfirmation:
        r.totalSupplierConfirmation ?? r.supplierQuantityConfirmation ?? r.supplierConfirmation ?? null,
      simulateTotalSupplierConfirmation:
        r.simulateTotalSupplierConfirmation ?? r.simulateSupplierQuantityConfirmation ?? r.supplierConfirmation ?? 0,
      originalForecast: r.originalForecast ?? r.totalSupplierForecastOriginal ?? 0,
      supplierQuantityConfirmation:
        r.supplierQuantityConfirmation ?? r.supplierConfirmation ?? r.totalSupplierConfirmation ?? 0,
      simulateSupplierQuantityConfirmation:
        r.simulateSupplierQuantityConfirmation ?? r.simulateTotalSupplierConfirmation ?? 0,

      totalFirmSupply:
        (r.totalFirmSupply ?? (() => {
          return (
            toNumber(r.poByGrDate) +
            toNumber(r.poPastDue)
          );
        })()),

      poByGrDate: r.poByGrDate ?? null,
      poPastDue: r.poPastDue ?? null,

      safetyStock: r.safetyStock ?? null,
      supplierConfirmationCumulative: r.supplierConfirmationCumulative ?? 0,
      cumulativeForecastOriginal: r.cumulativeForecastOriginal ?? 0,
      cumulativeForecastCurrent: r.cumulativeForecastCurrent ?? 0,
      deltaCumulativeOriginal: r.deltaCumulativeOriginal ?? 0,
      deltaCumulativeCurrent: r.deltaCumulativeCurrent ?? 0,
      projStockBasedOnConfirmation: r.projStockBasedOnConfirmation ?? 0,
      ssBreach: calcSsBreach(r.projStockBasedOnConfirmation, r.safetyStock),
      stockout: r.stockout ?? 0,
    }));
  }, [rows, formatWeekLabel, calcSsBreach, toNumber]);

  const handleCellValueChanged = useCallback(
    (params) => {
      if (params?.colDef?.field !== "simulateTotalSupplierConfirmation") return;
      // Ignore events that were triggered by our renderer's manual update to avoid double-processing
      if (params?.data && params.data.__skipOnSimulate) {
        // clear the sentinel and skip
        try { delete params.data.__skipOnSimulate; } catch (e) {}
        return;
      }
       const newValue = Number(params.newValue);
      const oldValue = Number(params.oldValue);
      const safeNew = Number.isFinite(newValue) ? newValue : 0;
      const safeOld = Number.isFinite(oldValue) ? oldValue : 0;

      const diff = safeNew - safeOld;
      const editedRowIndex = params.node?.rowIndex ?? 0;

      // Update cumulative + delta for edited row and below only
      params.api.forEachNode((node) => {
        if ((node.rowIndex ?? 0) < editedRowIndex) return;

        const currentCumulative = Number(node.data?.supplierConfirmationCumulative ?? 0);
        const updatedCumulative = currentCumulative + diff;

        node.setDataValue("supplierConfirmationCumulative", updatedCumulative);

        const cumulativeForecastOriginal = Number(node.data?.cumulativeForecastOriginal ?? 0);
        const updatedDelta = updatedCumulative - cumulativeForecastOriginal;

        node.setDataValue("deltaCumulativeOriginal", updatedDelta);
      
      
       const cumulativeForecastCurrent = Number(node.data?.cumulativeForecastCurrent ?? 0);
        node.setDataValue("deltaCumulativeCurrent", updatedCumulative - cumulativeForecastCurrent);

        const currentProj = Number(node.data?.projStockBasedOnConfirmation ?? 0);
        const updatedProj = currentProj + diff;
        node.setDataValue("projStockBasedOnConfirmation", updatedProj);

        const safetyStock = Number(node.data?.safetyStock ?? 0);
        node.setDataValue("ssBreach", calcSsBreach(updatedProj, safetyStock));
        node.setDataValue("stockout", calcStockout(updatedProj));
      
      });
const updatedRows = [];
      params.api.forEachNode((node) => updatedRows.push(node.data));
      onRowsUpdate?.(updatedRows);


      onSimulateChange?.(params.data?.id, safeNew);
    },
    [onSimulateChange, onRowsUpdate, calcSsBreach, calcStockout]
  );

  return (
    <Box sx={{ mt: 2 }}>
      <DataTable columns={columns} rows={tableData}
      immutableData={true}
      getRowId={(params) => params.data?.id}
      height={600}
      paginationProp={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            defaultColDef={{
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      excelMode: 'windows',
      suppressMiniFilter: false,
      buttons: ['apply', 'reset'],
    },
  }}
  suppressColumnMoveAnimation={true}
  groupDisplayType="multipleColumns"
   onCellValueChanged={handleCellValueChanged}
      />
    </Box>
  );
};

export default AFCAnalysisTable;